// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import { Group, Paper, Select, Stack, Text, TextInput, Title, Tooltip } from '@mantine/core';
import { IconCreditCard, IconShield } from '@tabler/icons-react';
import { DataTable } from 'mantine-datatable';
import type { JSX } from 'react';
import { useMemo, useState } from 'react';
import { Link } from 'react-router';
import { TableAnchor } from '../../../components/layout/TableAnchor';
import {
  type PaymentChargeEntry,
  type PaymentChargesFilters,
  type PaymentSource,
  type PaymentsTimeframe,
  type PaymentsVariant,
  usePaymentCharges,
} from '../../../hooks/usePaymentCharges';
import { formatMoney } from '../../../utils/chargeitems';
import styles from './PaymentsSection.module.css';

const PAGE_SIZE = 25;

export type PaymentRowAction =
  | 'review'
  | 'adjust'
  | 'record'
  | 'submit'
  | 'returned'
  | 'view'
  | 'viewClaim'
  | 'collect'
  | 'charge'
  | 'resend'
  | 'paid'
  | 'archive'
  | 'refund';

interface VariantConfig {
  title: string;
  description: string;
  emptyText: string;
  statusOptions: string[];
}

const VARIANT_CONFIG: Record<PaymentsVariant, VariantConfig> = {
  upcoming: {
    title: 'Upcoming Payments',
    description:
      "Insured claims moving through submission, payer return, and recording the patient's share. Self-pay never appears here — the card on file is charged automatically as each service happens.",
    emptyText: 'No upcoming payments.',
    statusOptions: ['Planned', 'Billed'],
  },
  outstanding: {
    title: 'Outstanding Payments',
    description:
      'Money still owed and awaiting payment from the patient — a self-pay auto-charge the card declined or underpaid, or an insured balance recorded after adjudication.',
    emptyText: 'No outstanding payments.',
    statusOptions: ['Billed'],
  },
  complete: {
    title: 'Complete Payments',
    description: 'Settled in full, or refunded. Receipts are filed to the patient chart.',
    emptyText: 'No completed payments.',
    statusOptions: ['Paid', 'Refunded'],
  },
  archived: {
    title: 'Archived Payments',
    description: 'Closed with a balance by staff decision. Nothing archives on its own.',
    emptyText: 'No archived payments.',
    statusOptions: ['Archived'],
  },
};

const TIMEFRAME_OPTIONS: { value: PaymentsTimeframe; label: string }[] = [
  { value: 'this-month', label: 'This Month' },
  { value: 'last-7-days', label: 'Last 7 Days' },
  { value: 'last-30-days', label: 'Last 30 Days' },
  { value: 'last-3-months', label: 'Last 3 Months' },
  { value: 'last-6-months', label: 'Last 6 Months' },
  { value: 'last-12-months', label: 'Last 12 Months' },
  { value: 'year-to-date', label: 'Year to Date' },
  { value: 'all', label: 'All Time' },
];

function formatDate(iso: string | undefined): string {
  if (!iso) {
    return '—';
  }
  return new Date(iso).toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
}

function formatPeriod(start: string | undefined, end: string | undefined): string | undefined {
  if (!start || !end) {
    return undefined;
  }
  const format = (value: string): string =>
    new Date(value).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  return `${format(start)} – ${format(end)}`;
}

export function formatStatus(entry: PaymentChargeEntry, variant: PaymentsVariant): string {
  if (entry.status === 'Archived') {
    return 'Archived with Balance';
  }
  if (entry.status === 'Paid') {
    return 'Paid';
  }
  if (entry.status === 'Refunded') {
    return 'Refunded';
  }
  if (entry.status === 'Planned') {
    return 'Scheduled';
  }
  if (entry.source === 'insured') {
    if (variant === 'upcoming') {
      if (entry.claimStatus === 'toSubmit') {
        return 'Awaiting Claim Submission';
      }
      if (entry.claimStatus === 'sent') {
        return 'Awaiting Claim Return';
      }
      if (entry.claimStatus === 'record') {
        return 'Record Balance';
      }
      if (!hasHistoryEvent(entry, 'claim submitted')) {
        return 'Awaiting Claim Submission';
      }
      if (!hasHistoryEvent(entry, 'claim returned')) {
        return 'Awaiting Claim Return';
      }
      return 'Record Balance';
    }
    if (entry.balanceStatus === 'Awaiting balance') {
      if (!hasHistoryEvent(entry, 'claim submitted')) {
        return 'Awaiting Claim Submission';
      }
      if (!hasHistoryEvent(entry, 'claim returned')) {
        return 'Awaiting Claim Return';
      }
      return 'Record Balance';
    }
    return 'Awaiting Payment';
  }
  if (variant === 'upcoming') {
    return 'Collect Payment';
  }
  return 'Awaiting Payment';
}

function statusClass(status: string): string {
  return status.toLowerCase().replaceAll(' ', '-');
}

function actionLabel(action: PaymentRowAction): string {
  return {
    review: 'Review',
    adjust: 'Adjust',
    record: 'Record balance',
    submit: 'Mark submitted',
    returned: 'Claim returned',
    view: 'View',
    viewClaim: 'View claim',
    collect: 'Collect',
    charge: 'Charge card',
    resend: 'Resend bill',
    paid: 'Mark paid',
    archive: 'Archive',
    refund: 'Refund',
  }[action];
}

function actionClass(action: PaymentRowAction): string | undefined {
  if (action === 'review') {
    return styles.reviewAction;
  }
  if (action === 'adjust' || action === 'archive') {
    return styles.mutedAction;
  }
  if (action === 'submit' || action === 'returned' || action === 'record') {
    return styles.maroonAction;
  }
  return styles.blueAction;
}

function formatBalance(entry: PaymentChargeEntry): string {
  if (entry.source === 'insured' && entry.balanceStatus === 'Awaiting balance') {
    return 'pending';
  }
  return formatMoney(entry.balanceCents / 100);
}

function formatInsurancePaid(entry: PaymentChargeEntry): string {
  if (entry.insuranceContributionCents !== undefined) {
    return formatMoney(entry.insuranceContributionCents / 100);
  }
  return entry.source === 'self-pay' ? 'n/a' : '–';
}

function hasHistoryEvent(entry: PaymentChargeEntry, text: string): boolean {
  return entry.history.some((item) => item.event.toLowerCase().includes(text));
}

function historyDate(entry: PaymentChargeEntry, text: string): string | undefined {
  return [...entry.history].reverse().find((item) => item.event.toLowerCase().includes(text))?.date;
}

function OutreachValue({ entry }: { entry: PaymentChargeEntry }): JSX.Element {
  return (
    <Text fw={entry.outreachAttempts >= 3 ? 700 : 400} c={entry.outreachAttempts >= 3 ? 'red' : undefined}>
      {entry.outreachAttempts}
    </Text>
  );
}

export interface PaymentsSectionProps {
  variant: PaymentsVariant;
  source: PaymentSource;
  onBalanceAction?: (entry: PaymentChargeEntry) => void;
  onAction?: (entry: PaymentChargeEntry, action: PaymentRowAction) => void;
  canRecordBalance?: boolean;
  globalFilters?: PaymentChargesFilters;
  showFilters?: boolean;
  onClaimReturned?: (entry: PaymentChargeEntry) => void;
  onClaimAction?: (entry: PaymentChargeEntry, action: 'submit' | 'returned') => void;
}

export function PaymentsSection({
  variant,
  source,
  onBalanceAction,
  onAction,
  canRecordBalance = true,
  globalFilters,
  showFilters = true,
  onClaimReturned,
  onClaimAction,
}: PaymentsSectionProps): JSX.Element {
  const config = VARIANT_CONFIG[variant];

  const [page, setPage] = useState(1);
  // Billing is an historical ledger. Start on All Time so prior appointments
  // and chart-recorded payments are visible on first load; users can narrow it.
  const [timeframe, setTimeframe] = useState<PaymentsTimeframe>('all');
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [patientName, setPatientName] = useState('');
  const [encounterType, setEncounterType] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const statusOptions = useMemo(() => {
    if (variant === 'archived') {
      return ['Archived with Balance'];
    }
    if (variant === 'complete') {
      return ['Paid', 'Refunded'];
    }
    if (variant === 'upcoming') {
      return source === 'self-pay'
        ? ['Collect Payment']
        : ['Awaiting Claim Submission', 'Awaiting Claim Return', 'Record Balance'];
    }
    return source === 'self-pay' ? ['Awaiting Payment'] : ['Awaiting Payment'];
  }, [source, variant]);

  const localFilters: PaymentChargesFilters = {
    timeframe,
    source,
    status: statusFilter,
    patientName,
    encounterType,
    search,
  };
  const { entries, total, loading } = usePaymentCharges({
    variant,
    page,
    pageSize: PAGE_SIZE,
    filters: globalFilters ?? localFilters,
  });

  const encounterTypeOptions = useMemo(
    () =>
      [
        ...new Set(entries.map((entry) => entry.encounterType).filter((value): value is string => Boolean(value))),
      ].sort(),
    [entries]
  );

  const filteredEntries = useMemo(() => {
    return entries.filter((e) => {
      if (statusFilter && formatStatus(e, variant) !== statusFilter) {
        return false;
      }
      if (patientName && !e.patientName.toLowerCase().includes(patientName.toLowerCase())) {
        return false;
      }
      if (encounterType && e.encounterType !== encounterType) {
        return false;
      }
      if (search) {
        const q = search.toLowerCase();
        if (
          !e.patientName.toLowerCase().includes(q) &&
          !e.chargeId.toLowerCase().includes(q) &&
          !e.encounterType?.toLowerCase().includes(q) &&
          !e.payer?.toLowerCase().includes(q)
        ) {
          return false;
        }
      }
      return true;
    });
  }, [entries, statusFilter, patientName, encounterType, search, variant]);

  return (
    <Paper bg="white" className={styles.section}>
      <Stack gap={0}>
        <Group justify="space-between" align="flex-start" gap="xl" wrap="wrap">
          <div>
            <Title order={2} fw={700} className={styles.sectionTitle}>
              {config.title}
            </Title>
            <Text c="dimmed" className={styles.description}>
              {config.description}
            </Text>
          </div>
          <Text size="xs" fw={600} c="dimmed" pt={6}>
            {total} charge{total === 1 ? '' : 's'}
          </Text>
        </Group>
        {showFilters && (
          <Group gap="sm" className={styles.filters}>
            <Select
              label="Timeframe"
              data={TIMEFRAME_OPTIONS}
              value={timeframe}
              size="sm"
              w={140}
              onChange={(v) => {
                setTimeframe((v ?? 'this-month') as PaymentsTimeframe);
                setPage(1);
              }}
              comboboxProps={{ withinPortal: false, position: 'bottom-start', shadow: 'sm' }}
            />
            <Select
              label="Status"
              placeholder="Status"
              data={statusOptions}
              value={statusFilter}
              size="sm"
              w={155}
              clearable
              onChange={(v) => {
                setStatusFilter(v);
                setPage(1);
              }}
              comboboxProps={{ withinPortal: false, position: 'bottom-start', shadow: 'sm' }}
            />
            <TextInput
              label="Patient"
              placeholder="Patient"
              value={patientName}
              size="sm"
              w={155}
              onChange={(e) => {
                setPatientName(e.currentTarget.value);
                setPage(1);
              }}
            />
            <Select
              label="Encounter Type"
              placeholder="—"
              data={encounterTypeOptions}
              value={encounterType}
              size="sm"
              w={175}
              clearable
              onChange={(v) => {
                setEncounterType(v);
                setPage(1);
              }}
              comboboxProps={{ withinPortal: false, position: 'bottom-start', shadow: 'sm' }}
            />
            <TextInput
              label="Search"
              placeholder="Search"
              value={search}
              size="sm"
              w={155}
              onChange={(e) => {
                setSearch(e.currentTarget.value);
                setPage(1);
              }}
            />
          </Group>
        )}

        <DataTable
          fetching={loading}
          minHeight={200}
          records={filteredEntries}
          idAccessor="chargeItemId"
          striped
          highlightOnHover
          fz="sm"
          noRecordsText={config.emptyText}
          totalRecords={total}
          recordsPerPage={PAGE_SIZE}
          page={page}
          onPageChange={setPage}
          className={total <= PAGE_SIZE ? `${styles.table} ${styles.tableNoPagination}` : styles.table}
          columns={[
            {
              accessor: 'created',
              title: 'Created',
              render: ({ created }: PaymentChargeEntry) => formatDate(created),
            },
            {
              accessor: 'chargeId',
              title: 'Charge ID',
              render: ({ chargeId }: PaymentChargeEntry) => chargeId,
            },
            {
              accessor: 'status',
              title: 'Status',
              render: (entry: PaymentChargeEntry) => {
                const label = formatStatus(entry, variant);
                let subline: string | undefined;
                if (entry.refundedCents > 0) {
                  subline = `${formatMoney(entry.refundedCents / 100)} refunded`;
                } else if (label === 'Awaiting Claim Return') {
                  const submitted = historyDate(entry, 'claim submitted');
                  subline = submitted ? `sent ${formatDate(submitted)}` : undefined;
                } else if (label === 'Record Balance') {
                  const returned = historyDate(entry, 'claim returned');
                  subline = returned ? `returned ${formatDate(returned)}` : undefined;
                } else if (label === 'Awaiting Payment' && entry.dueDate) {
                  subline = `due ${formatDate(entry.dueDate)}`;
                }
                return (
                  <Stack gap={2}>
                    <span className={`${styles.statusPill} ${styles[statusClass(label)] ?? ''}`}>{label}</span>
                    {subline && (
                      <Text size="xs" c="dimmed">
                        {subline}
                      </Text>
                    )}
                  </Stack>
                );
              },
            },
            {
              accessor: 'patientName',
              title: 'Patient / ID',
              render: ({ patientId, patientName: name, patientIdentifier }: PaymentChargeEntry) => (
                <Tooltip label={`Patient ID: ${patientIdentifier}`} openDelay={250} withArrow>
                  <Stack gap={0}>
                    <TableAnchor component={Link} to={`/Patient/${patientId}`} size="sm">
                      {name}
                    </TableAnchor>
                    <Text size="xs" c="dimmed">
                      {patientIdentifier}
                    </Text>
                  </Stack>
                </Tooltip>
              ),
            },
            {
              accessor: 'payer',
              title: 'Payer',
              render: ({ payer, source: entrySource }: PaymentChargeEntry) => (
                <span className={styles.payerCell}>
                  {entrySource === 'insured' ? <IconShield size={14} /> : <IconCreditCard size={14} />}
                  {entrySource === 'insured' ? (payer ?? 'Insurance') : 'Self-pay'}
                </span>
              ),
            },
            {
              accessor: 'encounterType',
              title: 'Encounter Type',
              render: ({ encounterType: et, periodStart, periodEnd }: PaymentChargeEntry) => (
                <Stack gap={0}>
                  <Text>{et ?? '—'}</Text>
                  {formatPeriod(periodStart, periodEnd) && (
                    <Text size="xs" c="dimmed">
                      {formatPeriod(periodStart, periodEnd)}
                    </Text>
                  )}
                </Stack>
              ),
            },
            {
              accessor: 'dateOfService',
              title: 'Date of Service',
              render: ({ dateOfService }: PaymentChargeEntry) => formatDate(dateOfService),
            },
            {
              accessor: 'billedCents',
              title: 'Billed',
              render: ({ billedCents }: PaymentChargeEntry) => formatMoney(billedCents / 100),
            },
            ...(source === 'self-pay'
              ? []
              : [
                  {
                    accessor: 'insuranceContributionCents',
                    title: 'Ins. Paid',
                    render: formatInsurancePaid,
                  },
                ]),
            {
              accessor: 'receivedCents',
              title: 'Received',
              render: ({ receivedCents }: PaymentChargeEntry) =>
                receivedCents > 0 ? formatMoney(receivedCents / 100) : '–',
            },
            ...(source === 'self-pay' ? [] : [{ accessor: 'balanceCents', title: 'Balance', render: formatBalance }]),
            {
              accessor: 'outreach',
              title: 'Outreach',
              render: (entry: PaymentChargeEntry) => <OutreachValue entry={entry} />,
            },
            {
              accessor: 'actions',
              title: 'Actions',
              render: (entry: PaymentChargeEntry) => {
                const actions: PaymentRowAction[] = [];
                if (variant === 'complete' || variant === 'archived') {
                  actions.push('review');
                  if (entry.receivedCents > entry.refundedCents) {
                    actions.push('refund');
                  }
                } else if (variant === 'upcoming' && entry.source === 'insured') {
                  actions.push('review');
                  if (
                    entry.claimStatus === 'toSubmit' ||
                    (!entry.claimStatus && !hasHistoryEvent(entry, 'claim submitted'))
                  ) {
                    actions.push(...(entry.claimId ? ['viewClaim' as const] : []), 'submit');
                  } else if (
                    entry.claimStatus === 'sent' ||
                    (!entry.claimStatus && !hasHistoryEvent(entry, 'claim returned'))
                  ) {
                    actions.push(...(entry.claimId ? ['viewClaim' as const] : []), 'returned');
                  } else if (canRecordBalance) {
                    actions.push('record');
                  }
                } else if (entry.source === 'insured' && entry.balanceStatus === 'Awaiting balance') {
                  actions.push('review');
                  if (canRecordBalance) {
                    actions.push('record');
                  }
                } else {
                  actions.push('review', 'charge', 'resend');
                  if (entry.receivedCents > entry.refundedCents) {
                    actions.push('refund');
                  }
                  actions.push('archive');
                }
                return (
                  <Group className={styles.actions} gap={0} wrap="nowrap">
                    {actions.map((action) =>
                      action === 'viewClaim' ? (
                        <TableAnchor
                          key={action}
                          component={Link}
                          to={`/claims/${entry.claimId}`}
                          className={actionClass(action)}
                          underline="never"
                          size="sm"
                        >
                          {actionLabel(action)}
                        </TableAnchor>
                      ) : (
                        <TableAnchor
                          key={action}
                          component="button"
                          className={actionClass(action)}
                          underline="never"
                          onClick={() => {
                            if (action === 'record' || action === 'view') {
                              onBalanceAction?.(entry);
                            } else if (action === 'submit' || action === 'returned') {
                              onClaimAction?.(entry, action);
                            } else {
                              onAction?.(entry, action);
                            }
                          }}
                          size="sm"
                        >
                          {actionLabel(action)}
                        </TableAnchor>
                      )
                    )}
                  </Group>
                );
              },
            },
          ]}
        />
      </Stack>
    </Paper>
  );
}
